import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import HttpClient from "../network/http";
import { fetchCsrfToken } from "../context/AuthContext";
import SchoolProfileService from "../service/schoolProfile";

const baseURL = process.env.REACT_APP_BASE_URL;

function shouldRetryProfileQuery(failureCount, error) {
  const status = error?.status ?? error?.response?.status;
  if (status === 401 || status === 403) return false;
  return failureCount < 3;
}

export default function useSchoolProfile({
  schoolid,
  enableAdminProfile = false,
} = {}) {
  const queryClient = useQueryClient();
  const httpClient = new HttpClient(baseURL, () => fetchCsrfToken());
  const service = new SchoolProfileService(httpClient);

  const profileQuery = useQuery(
    ["schoolProfileAdmin", schoolid],
    () => service.getAdminProfile(schoolid),
    {
      enabled: !!schoolid && enableAdminProfile,
      staleTime: 1000 * 60,
      retry: shouldRetryProfileQuery,
    },
  );

  const upsertProfileMutation = useMutation(
    (payload) => service.upsertAdminProfile(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["schoolProfileAdmin", schoolid]);
        queryClient.invalidateQueries(["schoolProfilePublic", schoolid]);
      },
    },
  );

  const publicProfileQuery = useQuery(
    ["schoolProfilePublic", schoolid],
    () => service.getPublicProfileBySchoolId(schoolid),
    {
      enabled: !!schoolid,
      staleTime: 1000 * 60,
      retry: shouldRetryProfileQuery,
    },
  );

  return {
    profileQuery,
    upsertProfileMutation,
    publicProfileQuery,
  };
}
